/**
 * useCollections Hook
 * React Query hook for managing collections
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { CollectionSummary, CreateCollectionRequest } from "../types/admin";

const API_BASE = "/api/collection";

/**
 * Fetch all collections
 */
async function fetchCollections(): Promise<CollectionSummary[]> {
  const response = await fetch(API_BASE, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new collection
 */
async function createCollection(data: CreateCollectionRequest): Promise<CollectionSummary> {
  const response = await fetch(API_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Fout bij aanmaken collectie");
  }

  return response.json();
}

/**
 * Hook to fetch all collections
 */
export function useCollections() {
  return useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });
}

/**
 * Hook to create a collection
 */
export function useCreateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCollection,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });
}
